import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  CalendarPlus,
  Pencil,
  Plus,
  X,
  Bell,
  ArrowsClockwise,
  Trash,
  CheckCircle,
  Note,
  Copy,
  Printer,
} from "@phosphor-icons/react";

const ALLO = "__ALLO__";

function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function plusOneYearISO() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function ContractDialog({
  open,
  onOpenChange,
  initial,
  kinds,
  onSave,
  onRenew,
  onDelete,
  onPrint,
  variant = "contract", // "contract" | "code"
}) {
  const isCode = variant === "code";
  const [copiedFeedback, setCopiedFeedback] = useState(false);
  const [form, setForm] = useState({
    customer: "",
    code: "",
    phone: "",
    activation_date: "",
    expiry_date: "",
    amount: "",
    notification_date: "",
    notes: "",
    codes: "",
    customer_notified: false,
  });
  const [items, setItems] = useState([]);
  const [pickerValue, setPickerValue] = useState("");
  const [pickerQty, setPickerQty] = useState(1);
  const [customKind, setCustomKind] = useState("");
  const [pickingAllo, setPickingAllo] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        customer: initial.customer || "",
        code: initial.code || "",
        phone: initial.phone || "",
        activation_date: initial.activation_date || "",
        expiry_date: initial.expiry_date || "",
        amount: initial.amount ? String(initial.amount) : "",
        notification_date: initial.notification_date || "",
        notes: initial.notes || "",
        codes: initial.codes || "",
        customer_notified: !!initial.customer_notified,
      });
      if (Array.isArray(initial.items) && initial.items.length) {
        setItems(
          initial.items.map((it) => ({
            kind: it.kind,
            qty: Math.max(1, parseInt(it.qty || 1, 10)),
          }))
        );
      } else if (initial.kind) {
        setItems([{ kind: initial.kind, qty: 1 }]);
      } else {
        setItems([]);
      }
    } else {
      setForm({
        customer: "",
        code: "",
        phone: "",
        activation_date: todayISO(),
        expiry_date: plusOneYearISO(),
        amount: "",
        notification_date: "",
        notes: "",
        codes: "",
        customer_notified: false,
      });
      setItems([]);
    }
    setPickerValue("");
    setPickerQty(1);
    setCustomKind("");
    setPickingAllo(false);
  }, [open, initial]);

  const allKinds = useMemo(
    () => [...new Set([...(kinds?.defaults || []), ...(kinds?.custom || [])])],
    [kinds]
  );

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addItem = () => {
    const chosen = pickingAllo ? customKind.trim() : pickerValue;
    if (!chosen) return;
    const qty = Math.max(1, parseInt(pickerQty, 10) || 1);
    setItems((prev) => {
      const idx = prev.findIndex(
        (it) => it.kind.toLowerCase() === chosen.toLowerCase()
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [...prev, { kind: chosen, qty }];
    });
    setPickerValue("");
    setPickerQty(1);
    setCustomKind("");
    setPickingAllo(false);
  };

  const removeItem = (i) =>
    setItems((prev) => prev.filter((_, idx) => idx !== i));

  const updateItemQty = (i, delta) =>
    setItems((prev) =>
      prev.map((it, idx) =>
        idx === i ? { ...it, qty: Math.max(1, it.qty + delta) } : it
      )
    );

  const canAddItem =
    (pickingAllo ? customKind.trim().length > 0 : !!pickerValue) &&
    pickerQty > 0;

  const canSubmit = isCode
    ? !!form.customer.trim()
    : form.customer.trim() &&
      form.activation_date &&
      form.expiry_date &&
      items.length > 0;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (isCode) {
      onSave(
        {
          customer: form.customer.trim(),
          code: form.code.trim(),
          phone: form.phone.trim(),
          activation_date: form.activation_date,
          expiry_date: form.expiry_date,
          items: items.map((it) => ({ kind: it.kind, qty: it.qty })),
          notes: form.notes || "",
          codes: form.codes || "",
        },
        initial?.id
      );
      return;
    }
    onSave(
      {
        customer: form.customer.trim(),
        code: form.code.trim(),
        phone: form.phone.trim(),
        activation_date: form.activation_date,
        expiry_date: form.expiry_date,
        items: items.map((it) => ({ kind: it.kind, qty: it.qty })),
        amount: parseFloat(form.amount) || 0,
        notification_date: form.notification_date || "",
        notes: form.notes || "",
        customer_notified: !!form.customer_notified,
      },
      initial?.id
    );
  };

  const isEdit = !!initial;

  const handlePickerChange = (v) => {
    if (v === ALLO) {
      setPickingAllo(true);
      setPickerValue("");
    } else {
      setPickingAllo(false);
      setPickerValue(v);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[680px] rounded-2xl p-0 overflow-hidden border-none max-h-[92vh] overflow-y-auto"
        data-testid="contract-dialog"
      >
        <div className="px-7 pt-6 pb-3 bg-ink text-white sticky top-0 z-10">
          <DialogHeader>
            <DialogTitle className="font-caveat text-3xl font-bold flex items-center gap-2">
              {isEdit ? (
                <Pencil size={22} weight="bold" />
              ) : (
                <CalendarPlus size={22} weight="bold" />
              )}
              {isEdit
                ? isCode
                  ? "ΕΓΓΡΑΦΗ ΚΩΔΙΚΩΝ"
                  : "ΣΥΜΒΟΛΑΙΟ"
                : isCode
                ? "ΝΕΑ ΕΓΓΡΑΦΗ ΚΩΔΙΚΩΝ"
                : "ΝΕΟ ΣΥΜΒΟΛΑΙΟ"}
            </DialogTitle>
            <DialogDescription className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              {isEdit
                ? form.customer ||
                  (isCode ? "ΕΠΕΞΕΡΓΑΣΙΑ ΚΩΔΙΚΩΝ" : "ΕΠΕΞΕΡΓΑΣΙΑ ΣΥΜΒΟΛΑΙΟΥ")
                : isCode
                ? "ΚΩΔΙΚΟΙ ΠΡΟΓΡΑΜΜΑΤΩΝ"
                : "ΑΝΑΝΕΩΣΗ / ΛΗΞΗ ΣΥΜΒΟΛΑΙΟΥ"}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-7 py-5 bg-white space-y-3">
          {/* Codes-variant action bar: copy + print + delete */}
          {isCode && (
            <div
              className="flex flex-wrap gap-2 -mt-1 mb-2"
              data-testid="code-actions-bar"
            >
              {form.codes && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(form.codes);
                      setCopiedFeedback(true);
                      setTimeout(() => setCopiedFeedback(false), 1400);
                    } catch {
                      /* ignore */
                    }
                  }}
                  data-testid="code-copy-btn"
                  className={`flex-1 min-w-[180px] px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 active:scale-[0.98] transition-all shadow-sm ${
                    copiedFeedback
                      ? "bg-green-600 text-white"
                      : "bg-sky-600 text-white hover:bg-sky-700"
                  }`}
                  title="ΑΝΤΙΓΡΑΦΗ ΚΩΔΙΚΩΝ ΣΤΟ CLIPBOARD"
                >
                  {copiedFeedback ? (
                    <>
                      <CheckCircle size={16} weight="bold" />
                      ΑΝΤΙΓΡΑΦΗΚΕ
                    </>
                  ) : (
                    <>
                      <Copy size={16} weight="bold" />
                      ΑΝΤΙΓΡΑΦΗ ΚΩΔΙΚΩΝ
                    </>
                  )}
                </button>
              )}
              {isEdit && (
                <button
                  type="button"
                  onClick={() => onPrint && onPrint(initial)}
                  data-testid="code-print-btn"
                  className="px-4 py-2.5 rounded-xl border-2 border-ink/20 bg-white text-ink font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-stone-50 active:scale-[0.98] transition-all"
                  title="ΕΚΤΥΠΩΣΗ ΚΑΡΤΑΣ"
                >
                  <Printer size={14} weight="bold" />
                  ΕΚΤΥΠΩΣΗ
                </button>
              )}
              {isEdit && (
                <button
                  type="button"
                  onClick={() => onDelete && onDelete(initial)}
                  data-testid="code-delete-btn"
                  className="px-4 py-2.5 rounded-xl border-2 border-red-200 bg-red-50 text-red-700 font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-red-100 active:scale-[0.98] transition-all"
                  title="ΔΙΑΓΡΑΦΗ ΕΓΓΡΑΦΗΣ"
                >
                  <Trash size={14} weight="bold" />
                  ΔΙΑΓΡΑΦΗ
                </button>
              )}
            </div>
          )}


          {/* Action bar — only when editing existing contract (not for codes variant) */}
          {isEdit && !isCode && (
            <div
              className="flex flex-wrap gap-2 -mt-1 mb-2"
              data-testid="contract-actions-bar"
            >
              <button
                type="button"
                onClick={() => onRenew && onRenew(initial)}
                data-testid="contract-renew-btn"
                className="flex-1 min-w-[180px] px-4 py-2.5 rounded-xl bg-green-600 text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-green-700 active:scale-[0.98] transition-all shadow-sm"
                title="ΔΗΜΙΟΥΡΓΙΑ POST-IT ΠΑΡΑΓΓΕΛΙΑΣ"
              >
                <ArrowsClockwise size={16} weight="bold" />
                ΝΑ ΓΙΝΕΙ ΑΝΑΝΕΩΣΗ
              </button>
              <button
                type="button"
                onClick={() => onDelete && onDelete(initial)}
                data-testid="contract-delete-btn"
                className="px-4 py-2.5 rounded-xl border-2 border-red-200 bg-red-50 text-red-700 font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-red-100 active:scale-[0.98] transition-all"
                title="ΔΙΑΓΡΑΦΗ ΣΥΜΒΟΛΑΙΟΥ"
              >
                <Trash size={14} weight="bold" />
                ΔΙΑΓΡΑΦΗ
              </button>
            </div>
          )}

          <Field label="ΠΕΛΑΤΗΣ *">
            <input
              type="text"
              autoFocus
              value={form.customer}
              onChange={(e) =>
                setField("customer", e.target.value.toUpperCase())
              }
              data-testid="contract-customer"
              className="w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial uppercase"
              placeholder="ΟΝΟΜΑ ΠΕΛΑΤΗ"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="ΚΩΔΙΚΟΣ">
              <input
                type="text"
                value={form.code}
                onChange={(e) => setField("code", e.target.value)}
                data-testid="contract-code"
                className="w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial"
                placeholder="—"
              />
            </Field>
            <Field label="ΤΗΛΕΦΩΝΟ">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                data-testid="contract-phone"
                className="w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial"
                placeholder="—"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="ΗΜ. ΕΝΕΡΓΟΠΟΙΗΣΗΣ *">
              <input
                type="date"
                value={form.activation_date}
                onChange={(e) => setField("activation_date", e.target.value)}
                data-testid="contract-activation"
                className="w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial"
              />
            </Field>
            <Field label="ΗΜ. ΛΗΞΗΣ *">
              <input
                type="date"
                value={form.expiry_date}
                onChange={(e) => setField("expiry_date", e.target.value)}
                data-testid="contract-expiry"
                className="w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial"
              />
            </Field>
          </div>

          <Field label={`ΕΙΔΗ ΣΥΜΒΟΛΑΙΟΥ * (${items.length})`}>
            {items.length > 0 && (
              <div
                className="flex flex-wrap gap-1.5 mb-2"
                data-testid="contract-items-list"
              >
                {items.map((it, i) => (
                  <div
                    key={`${it.kind}-${i}`}
                    data-testid={`contract-item-${i}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-full border-2 border-ink/15 bg-ink/[0.04]"
                  >
                    <span className="text-sm font-arial font-bold text-ink whitespace-nowrap">
                      {it.kind}
                    </span>
                    <div className="flex items-center gap-0.5 border-l border-ink/15 pl-2">
                      <button
                        type="button"
                        onClick={() => updateItemQty(i, -1)}
                        className="w-6 h-6 rounded text-ink/60 hover:bg-black/5 text-sm font-bold leading-none"
                      >
                        −
                      </button>
                      <span
                        data-testid={`contract-item-${i}-qty`}
                        className="text-sm font-bold tabular-nums min-w-[22px] text-center"
                      >
                        {it.qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateItemQty(i, 1)}
                        className="w-6 h-6 rounded text-ink/60 hover:bg-black/5 text-sm font-bold leading-none"
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(i)}
                      data-testid={`contract-item-${i}-remove`}
                      className="ml-0.5 w-6 h-6 rounded-full text-red-600/70 hover:bg-red-50 flex items-center justify-center"
                    >
                      <X size={13} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-stretch">
              <select
                value={pickingAllo ? ALLO : pickerValue}
                onChange={(e) => handlePickerChange(e.target.value)}
                data-testid="contract-kind"
                className="min-w-[260px] px-3 py-2.5 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-base font-bold font-arial uppercase tracking-wide"
              >
                <option value="">— ΕΠΙΛΕΞΕ ΕΙΔΟΣ —</option>
                {allKinds.map((k) => (
                  <option key={k} value={k} style={{ fontWeight: 700, fontSize: "15px" }}>
                    {k}
                  </option>
                ))}
                <option value={ALLO} style={{ fontWeight: 700, fontSize: "15px" }}>Άλλο…</option>
              </select>
              <input
                type="number"
                min="1"
                value={pickerQty}
                onChange={(e) =>
                  setPickerQty(Math.max(1, parseInt(e.target.value, 10) || 1))
                }
                data-testid="contract-kind-qty"
                className="w-20 px-2 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial text-center"
                placeholder="ΠΟΣ."
              />
              <button
                type="button"
                onClick={addItem}
                disabled={!canAddItem}
                data-testid="contract-kind-add"
                className={`px-3 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1 transition-all ${
                  canAddItem
                    ? "bg-ink text-white hover:bg-ink/85 active:scale-95"
                    : "bg-ink/15 text-white/70 cursor-not-allowed"
                }`}
              >
                <Plus size={14} weight="bold" />
              </button>
            </div>
            {pickingAllo && (
              <input
                type="text"
                autoFocus
                value={customKind}
                onChange={(e) => setCustomKind(e.target.value)}
                data-testid="contract-kind-custom"
                placeholder="ΓΡΑΨΕ ΤΟ ΕΙΔΟΣ ΣΥΜΒΟΛΑΙΟΥ..."
                className="mt-2 w-full px-3 py-2 rounded-xl border border-amber-300 bg-amber-50/60 focus:border-amber-500 outline-none text-sm font-arial"
              />
            )}
          </Field>

          {!isCode && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="ΠΟΣΟ (€)">
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setField("amount", e.target.value)}
                data-testid="contract-amount"
                className="w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial tabular-nums"
                placeholder="0.00"
              />
            </Field>
            <Field
              label={
                <span className="inline-flex items-center gap-1">
                  <Bell size={11} weight="bold" /> ΕΝΗΜΕΡΩΣΗ
                </span>
              }
            >
              <input
                type="date"
                value={form.notification_date}
                onChange={(e) => setField("notification_date", e.target.value)}
                data-testid="contract-notification"
                className="w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial"
              />
            </Field>
          </div>
          )}

          {/* ΕΝΗΜΕΡΩΘΗΚΕ Ο ΠΕΛΑΤΗΣ */}
          {!isCode && (
          <label
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
              form.customer_notified
                ? "bg-green-50 border-green-300"
                : "bg-white border-ink/15 hover:border-ink/30"
            }`}
            data-testid="contract-notified-label"
          >
            <input
              type="checkbox"
              checked={form.customer_notified}
              onChange={(e) => setField("customer_notified", e.target.checked)}
              data-testid="contract-notified"
              className="w-4 h-4 accent-green-600"
            />
            <CheckCircle
              size={16}
              weight={form.customer_notified ? "fill" : "bold"}
              className={
                form.customer_notified ? "text-green-600" : "text-ink/40"
              }
            />
            <span
              className={`text-xs font-black uppercase tracking-wider ${
                form.customer_notified ? "text-green-800" : "text-ink/65"
              }`}
            >
              ΕΝΗΜΕΡΩΘΗΚΕ Ο ΠΕΛΑΤΗΣ
            </span>
          </label>
          )}

          {/* ΚΩΔΙΚΟΙ — codes variant only */}
          {isCode && (
            <Field label="ΚΩΔΙΚΟΙ">
              <textarea
                value={form.codes}
                onChange={(e) => setField("codes", e.target.value)}
                data-testid="contract-codes-field"
                rows={3}
                className="w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-mono resize-y min-h-[68px]"
                placeholder="USERNAME, PASSWORD, KEY ΠΡΟΓΡΑΜΜΑΤΟΣ..."
              />
            </Field>
          )}

          {/* ΣΗΜΕΙΩΣΕΙΣ */}
          <Field
            label={
              <span className="inline-flex items-center gap-1">
                <Note size={11} weight="bold" /> ΣΗΜΕΙΩΣΕΙΣ
              </span>
            }
          >
            <textarea
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              data-testid="contract-notes"
              rows={isCode ? 8 : 3}
              className={`w-full px-3 py-2 rounded-xl border border-ink/15 bg-white focus:border-ink/50 outline-none text-sm font-arial resize-y ${
                isCode ? "min-h-[200px]" : "min-h-[68px]"
              }`}
              placeholder="ΕΛΕΥΘΕΡΟ ΚΕΙΜΕΝΟ — ΠΑΡΑΤΗΡΗΣΕΙΣ, ΣΥΜΦΩΝΙΕΣ, ΕΠΑΦΗ..."
            />
          </Field>

          <DialogFooter className="!justify-end gap-2 !mt-5">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="contract-cancel"
              className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-ink/70 hover:text-ink hover:bg-black/5 transition-colors"
            >
              ΑΚΥΡΟ
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="contract-save"
              className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                canSubmit
                  ? "bg-ink text-white hover:bg-ink/85 active:scale-95"
                  : "bg-ink/20 text-white/70 cursor-not-allowed"
              }`}
            >
              {isEdit ? "ΑΠΟΘΗΚΕΥΣΗ" : "ΚΑΤΑΧΩΡΗΣΗ"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
